import type { SeriesWithSlideDeck } from '@mentimeter/presentation-schema/api-types-overrides';
import type { Series } from '@mentimeter/legacy-series-types/series';
import type { SlideDeck as InternalSlideDeck } from '../../../compatibility-overrides/presentation-types';
import { isProxyInstance } from '../../../utils/is-proxy-instance';
import { baseSeriesCompatibilityLayerHandler } from '../../common/base-compatibility-layer-handler';
import { createPresentationSlideCompatibilityLayer } from '../slide-compatibility-layer/create-presentation-slide-compatibility-layer';
import { getOrSetCache, type WithCache } from '../../../utils/get-or-set-cache';
import { propertyToSeriesProperty as slideDeckPropertyToSeriesProperty } from '../../../backward-compatibility/converters/slide-deck/property-to-series-property';
import { collaborationModeToCoEdited } from '../../../backward-compatibility/converters/slide-deck';

export const createPresentationSeriesCompatibilityLayer = (
  _target: unknown,
): SeriesWithSlideDeck | undefined => {
  const target = _target as Series | InternalSlideDeck | undefined;

  if (!target) {
    return undefined;
  }

  if (isProxyInstance(target)) {
    return target as unknown as SeriesWithSlideDeck;
  }

  return new Proxy(
    target,
    baseSeriesCompatibilityLayerHandler<InternalSlideDeck>({
      getMigratedSeriesProperty: (target, key) => {
        if (key === 'presentation_time') {
          return target.presentationTimer.presentationStartedAt;
        }

        if (key === 'slides') {
          const slides = (
            target.slides as Array<
              (typeof target.slides)[number] & { is_migrated: boolean }
            >
          ).map((slide) => createPresentationSlideCompatibilityLayer(slide));
          return slides;
        }

        if (key === 'questions') {
          const questions = target.slides.map((slide) =>
            createPresentationSlideCompatibilityLayer(slide),
          );

          const cacheKey = JSON.stringify(questions);
          return getOrSetCache(
            target as WithCache<InternalSlideDeck>,
            'questions',
            cacheKey,
            questions,
          );
        }

        if (key === 'co_edited') {
          return collaborationModeToCoEdited(
            target.ownershipSettings?.collaborationMode,
          );
        }

        return slideDeckPropertyToSeriesProperty(target, key);
      },
    }),
  ) as unknown as SeriesWithSlideDeck;
};
